/**
 * @ares/i18n — Internationalization Loader
 *
 * Multi-Locale + Multi-Voice Translation-System.
 * Phase 3 Sprint A · 2026-04-26 · Stub-Mode (file-import).
 *
 * Locales:
 *   - en           (default, English)
 *   - de-ares      (Deutsch, Spartan-Voice)
 *   - de-salutem   (Deutsch, Klinik-Voice — extends de-ares)
 *
 * Usage:
 *   import { t, setLocale, getLocale } from '@ares/i18n';
 *   setLocale('de-ares');
 *   t('nav.missionBrief');                       // → "Mission Brief"
 *   t('hermes.modelLabel', { model: 'gemini-3-flash', latencyMs: 96 });
 */

import enStrings from './en.json';
import enMedicStrings from './en-medic.json';
import deAresStrings from './de-ares.json';
import deMedicStrings from './de-medic.json';
import deSalutemStrings from './de-salutem.json';

// ============================================================
// TYPES
// ============================================================

export type Locale = 'en' | 'en-medic' | 'de-ares' | 'de-medic' | 'de-salutem' | (string & {});
export type Tenant = 'ares' | 'salutem' | (string & {});

export interface LocaleStrings {
  $schema?: string;
  locale: Locale;
  name: string;
  tenant?: Tenant;
  version: string;
  voice?: string;
  extends?: Locale;
  [key: string]: unknown;
}

// ============================================================
// REGISTRY
// ============================================================

const LOCALE_REGISTRY: Record<Locale, LocaleStrings> = {
  // Default Voices
  'en': enStrings as LocaleStrings,
  'de-ares': deAresStrings as LocaleStrings,
  // Medic-Voices (auto-activated when User picks Theme=medic im Settings)
  'en-medic': enMedicStrings as LocaleStrings,
  'de-medic': deMedicStrings as LocaleStrings,
  // Salutem-Tenant-spezifisch (extends de-medic)
  'de-salutem': deSalutemStrings as LocaleStrings,
};

let CURRENT_LOCALE: Locale = 'en';

// ============================================================
// CASCADE-RESOLUTION
// ============================================================

/**
 * Returns merged strings for a locale (resolves `extends` cascade).
 * de-salutem extends de-ares — falls back for missing keys.
 */
function resolveLocale(locale: Locale): LocaleStrings {
  const direct = LOCALE_REGISTRY[locale];
  if (!direct) {
    console.warn(`[ares/i18n] Unknown locale "${locale}", falling back to "en".`);
    return LOCALE_REGISTRY['en'];
  }
  if (direct.extends) {
    const parent = resolveLocale(direct.extends);
    return mergeStrings(parent, direct);
  }
  return direct;
}

function mergeStrings(parent: LocaleStrings, child: LocaleStrings): LocaleStrings {
  const merged = JSON.parse(JSON.stringify(parent));

  function recursiveMerge(target: any, source: any): void {
    for (const key in source) {
      if (['$schema', 'extends', 'extends-strategy'].includes(key)) continue;
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        if (!target[key] || typeof target[key] !== 'object') target[key] = {};
        recursiveMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }

  recursiveMerge(merged, child);
  merged.locale = child.locale;
  merged.name = child.name;
  return merged;
}

// ============================================================
// LOCALE-DETECTION
// ============================================================

/**
 * Best-Effort Locale-Detection.
 * Order: explicit user-pref → tenant-default → browser-locale → fallback (en).
 */
export function detectLocale(opts: {
  userPref?: Locale;
  tenant?: Tenant;
  themeMode?: 'light' | 'dark' | 'medic';
  browserLanguage?: string;
} = {}): Locale {
  if (opts.userPref && LOCALE_REGISTRY[opts.userPref]) return opts.userPref;

  // Tenant-Default
  if (opts.tenant === 'salutem') return 'de-salutem';

  // Theme-Coupling: wenn User Theme=medic gewählt → Medic-Voice
  if (opts.themeMode === 'medic') {
    const browser = opts.browserLanguage ??
      (typeof navigator !== 'undefined' ? navigator.language : 'en');
    return browser.startsWith('de') ? 'de-medic' : 'en-medic';
  }

  // ARES-Default: Browser-Locale
  if (opts.tenant === 'ares' || !opts.tenant) {
    const browser = opts.browserLanguage ??
      (typeof navigator !== 'undefined' ? navigator.language : 'en');
    if (browser.startsWith('de')) return 'de-ares';
    return 'en';
  }

  return 'en';
}

// ============================================================
// SET / GET
// ============================================================

export function setLocale(locale: Locale): void {
  if (!LOCALE_REGISTRY[locale]) {
    console.warn(`[ares/i18n] setLocale: unknown locale "${locale}", ignoring.`);
    return;
  }
  CURRENT_LOCALE = locale;
  if (typeof document !== 'undefined') {
    const langPrefix = locale.split('-')[0] ?? 'en';
    document.documentElement.lang = langPrefix;
    document.documentElement.dataset.locale = locale;
  }
}

export function getLocale(): Locale {
  return CURRENT_LOCALE;
}

export function listLocales(): Locale[] {
  return Object.keys(LOCALE_REGISTRY);
}

export function registerLocale(locale: Locale, strings: LocaleStrings): void {
  LOCALE_REGISTRY[locale] = strings;
}

// ============================================================
// TRANSLATION
// ============================================================

/**
 * Translate a key with optional interpolation.
 * Key supports nested dot-notation: t('nav.missionBrief')
 * Interpolation: t('hermes.modelLabel', { model: 'X', latencyMs: 100 })
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const strings = resolveLocale(CURRENT_LOCALE);
  const parts = key.split('.');
  let node: any = strings;
  for (const part of parts) {
    if (node && typeof node === 'object' && part in node) {
      node = node[part];
    } else {
      // Try fallback to EN if not found
      if (CURRENT_LOCALE !== 'en') {
        const en = resolveLocale('en');
        let enNode: any = en;
        for (const p of parts) {
          if (enNode && typeof enNode === 'object' && p in enNode) {
            enNode = enNode[p];
          } else {
            enNode = null;
            break;
          }
        }
        if (typeof enNode === 'string') {
          console.warn(`[ares/i18n] Missing key "${key}" in locale "${CURRENT_LOCALE}", using EN fallback.`);
          return interpolate(enNode, vars);
        }
      }
      console.warn(`[ares/i18n] Missing key "${key}" — returning key as fallback.`);
      return key;
    }
  }
  if (typeof node !== 'string') {
    console.warn(`[ares/i18n] Key "${key}" resolved to non-string value, returning key.`);
    return key;
  }
  return interpolate(node, vars);
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    return vars[name] !== undefined ? String(vars[name]) : `{{${name}}}`;
  });
}

// ============================================================
// CONVENIENCE
// ============================================================

/** React-Hook-Style: liefert t-Function für aktuelle Locale (nützlich in React-Components). */
export function useTranslation() {
  return { t, locale: CURRENT_LOCALE, setLocale };
}

/** Gibt alle Sidebar-Nav-Labels für aktuelle Locale zurück (für Component-Rendering). */
export function getSidebarNavLabels(): Record<string, string> {
  const strings = resolveLocale(CURRENT_LOCALE);
  return (strings.nav as Record<string, string>) ?? {};
}

export const locales = LOCALE_REGISTRY;

export default {
  t,
  setLocale,
  getLocale,
  detectLocale,
  listLocales,
  registerLocale,
  useTranslation,
  getSidebarNavLabels,
  locales,
};
